import type { MediaImage } from '@efdigital/ll-addon-data';
import { service } from '@ember/service';
import Component from '@glimmer/component';
import { tracked } from '@glimmer/tracking';
import type { Store } from '@warp-drive/core';
import type { ReactiveDataDocument } from '@warp-drive/core/reactive';
import { type ContentFeatures, Request } from '@warp-drive/ember';

import { getPaginationTotal } from '../../helpers/get-pagination-total.ts';
import type MediaImagesService from '../../services/media-images';
import { PaginationByPageNumber } from '../pagination.gts';
import {
  SearchImages,
  type SearchImagesState,
} from '../search/search-images.gts';

interface ImageBrowserSignature {
  Element: HTMLDivElement;
  Args: {
    onImageSelected: (image: MediaImage) => Promise<void>;
  };
  Blocks: {
    item: [
      item: MediaImage,
      contentFeatures: ContentFeatures<ReactiveDataDocument<MediaImage[]>>,
    ];
  };
}

export class ImageBrowser extends Component<ImageBrowserSignature> {
  @service('media-images') declare mediaImages: MediaImagesService;
  @service declare store: Store;

  @tracked state: SearchImagesState = {
    pageIndex: 0,
    pageSize: 12,
    textFilter: '',
  };

  /**
   * Extract meaningful keywords from context title for smart suggestions.
   * Removes common words and extracts relevant nouns/adjectives.
   */
  extractContextKeywords(title: string): string {
    const stopWords = new Set([
      'the',
      'a',
      'an',
      'and',
      'or',
      'but',
      'in',
      'on',
      'at',
      'to',
      'for',
      'of',
      'with',
      'by',
      'from',
      'up',
      'about',
      'into',
      'through',
      'during',
    ]);

    return title
      .toLowerCase()
      .replace(/[^\w\s]/g, ' ') // Remove punctuation
      .split(/\s+/)
      .filter((word) => word.length > 2 && !stopWords.has(word))
      .slice(0, 3) // Take first 3 meaningful words
      .join(' ');
  }

  setPageIndex = (pageIndex: number): void => {
    this.setState({ pageIndex });
  };

  setState = (state: Partial<SearchImagesState>): void => {
    this.state = Object.assign(this.state, state);
  };

  <template>
    <SearchImages
      @legend="Images"
      @state={{this.state}}
      @setState={{this.setState}}
    >
      <:query as |query|>
        <Request @query={{query}}>
          <:content as |response state|>
            {{#if response.data.length}}
              <PaginationByPageNumber
                @pageIndex={{this.state.pageIndex}}
                @pageSize={{this.state.pageSize}}
                @total={{getPaginationTotal response}}
                @onPageIndexChange={{this.setPageIndex}}
                class="d-flex flex-row justify-content-center my-4"
              />

              {{! Image grid }}
              <div class="row g-3 mb-4">
                {{#each response.data as |item|}}
                  {{yield item state to="item"}}
                {{/each}}
              </div>

              <PaginationByPageNumber
                @pageIndex={{this.state.pageIndex}}
                @pageSize={{this.state.pageSize}}
                @total={{getPaginationTotal response}}
                @onPageIndexChange={{this.setPageIndex}}
                class="d-flex flex-row justify-content-center my-4"
              />
            {{else}}
              <div class="text-center py-5">
                <iconify-icon
                  inline
                  icon="ph:image-broken-duotone"
                  class="text-muted h1"
                />
                <p class="text-muted mt-3">
                  {{#if this.state.textFilter}}
                    No images found matching "{{this.state.textFilter}}".
                  {{else}}
                    No images available. Upload your first image to get started.
                  {{/if}}
                </p>
              </div>
            {{/if}}
          </:content>
          <:loading>
            <div class="modal-body">
              <div class="text-center py-5">
                <div class="spinner-border" role="status">
                  <span class="visually-hidden">Loading...</span>
                </div>
              </div>
            </div>
          </:loading>
          <:error as |error|>
            <div class="modal-body">
              <div class="text-center py-5">
                <p class="text-danger">Error loading images:
                  {{error.message}}</p>
              </div>
            </div>
          </:error>
        </Request>
      </:query>
    </SearchImages>
  </template>
}
